import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, HeartCrack, ChevronRight, BookOpen, Heart, Users, Baby, Sparkles, RefreshCw } from 'lucide-react';

const SEPARATION_CONTENT = [
  {
    id: 'decision',
    title: 'Att fatta beslutet',
    icon: Heart,
    description: 'Hur vet du om det är rätt att gå?',
    content: [
      '**Det finns inget perfekt svar:** Ibland vet man bara att det är dags.',
      '**Frågor att ställa dig själv:**',
      '• Har du försökt allt som står i din makt?',
      '• Kan du se dig själv i relationen om fem år?',
      '• Växer du eller krymper du i relationen?',
      '• Finns det respekt, tillit och kärlek kvar?',
      '**Kom ihåg:** Att lämna en relation som inte fungerar är inte att misslyckas.',
      '**Ambivalens är normalt:** Det är okej att ha blandade känslor.',
      '**Lyssna på din kropp:** Ständig ångest, magont, sömnproblem – kroppen vet.'
    ],
    questions: [
      'Vad håller dig kvar i relationen?',
      'Vad är du rädd för om du lämnar?',
      'Hur skulle du råda en vän i samma situation?',
      'Vad behöver du för att känna dig redo?'
    ]
  },
  {
    id: 'practical',
    title: 'Praktiska förberedelser',
    icon: Users,
    description: 'Vad du behöver tänka på.',
    content: [
      '**Ekonomi:** Skaffa koll på era gemensamma tillgångar och skulder.',
      '**Boende:** Vem ska bo kvar? Behöver du hitta ny bostad?',
      '**Juridik:** Kontakta en familjerättsadvokat om ni har barn eller gemensam egendom.',
      '**Stödsystem:** Berätta för vänner och familj så du har folk som stöttar dig.',
      '**Dokumentation:** Spara viktiga dokument (kontrakt, försäkringar, etc.).',
      '**Självständig ekonomi:** Öppna eget bankkonto om ni haft gemensamt.',
      '**Timing:** Planera när och hur du ska berätta – gärna inte i affekt.'
    ],
    questions: [
      'Har du koll på er gemensamma ekonomi?',
      'Var kan du bo om du flyttar?',
      'Vem kan stötta dig praktiskt och emotionellt?'
    ]
  },
  {
    id: 'children',
    title: 'När det finns barn',
    icon: Baby,
    description: 'Sätt barnens bästa i fokus.',
    content: [
      '**Barnen är inte budbärare:** Prata aldrig illa om den andre föräldern inför barnen.',
      '**Berätta tillsammans:** Om möjligt, berätta för barnen tillsammans.',
      '**Åldersanpassat:** Anpassa förklaringen efter barnets ålder.',
      '**Trygghet:** Betona att det inte är barnets fel och att båda älskar dem.',
      '**Rutiner:** Försök behålla rutiner och stabilitet.',
      '**Vårdnad:** Samarbeta kring vårdnad – barnet behöver båda föräldrarna.',
      '**Professionell hjälp:** Familjerådgivning kan hjälpa er kommunicera.',
      '**Dina känslor:** Bearbeta din sorg separat – barnen ska inte behöva trösta dig.'
    ],
    questions: [
      'Hur kan du skydda barnen från konflikten?',
      'Kan du och din ex samarbeta kring barnen?',
      'Behöver barnen prata med någon professionell?'
    ]
  },
  {
    id: 'grief',
    title: 'Sorgen efter separationen',
    icon: HeartCrack,
    description: 'Det är okej att sörja.',
    content: [
      '**Sorg är normalt:** Även om du ville separera så sörjer du.',
      '**Vågor av känslor:** Ilska, sorg, lättnad, skuld – ofta på samma dag.',
      '**Sörja framtiden:** Du sörjer inte bara relationen utan också drömmen om hur det skulle bli.',
      '**Ensamhet:** Den första tiden ensam kan vara brutal – det blir bättre.',
      '**Tillåt dig att gråta:** Tårar är läkande.',
      '**Undvik bedövning:** Alkohol, rebound-relationer, överarbete – det skjuter bara upp sorgen.',
      '**Det tar tid:** Det finns ingen genväg genom sorgen, bara igenom.'
    ],
    questions: [
      'Hur tillåter du dig själv att sörja?',
      'Vad saknar du mest?',
      'Vad är du lättad över?',
      'Hur kan du vara snäll mot dig själv just nu?'
    ]
  },
  {
    id: 'rebuild',
    title: 'Bygga upp dig själv igen',
    icon: RefreshCw,
    description: 'Återfinna dig själv efter separationen.',
    content: [
      '**Återupptäck dig själv:** Vem är du utan relationen? Vad gillar du?',
      '**Nya rutiner:** Skapa nya vardagsrutiner som är dina.',
      '**Socialt liv:** Återknyt kontakter du kanske försummat.',
      '**Fysisk hälsa:** Motion, sömn och mat – grunderna för att må bra.',
      '**Terapi:** En terapeut kan hjälpa dig bearbeta och växa.',
      '**Lär av relationen:** Vad tar du med dig? Vad vill du göra annorlunda?',
      '**Ta tid:** Rusa inte in i en ny relation – lär känna dig själv först.',
      '**Fira framsteg:** Även små steg framåt är värda att fira.'
    ],
    questions: [
      'Vad vill du göra nu som du inte kunde innan?',
      'Vilka delar av dig själv vill du återupptäcka?',
      'Vad har du lärt dig om dig själv?',
      'Hur ser ditt drömscenario ut om ett år?'
    ]
  },
  {
    id: 'future',
    title: 'Kärleken finns där ute',
    icon: Sparkles,
    description: 'Hoppfullhet inför framtiden.',
    content: [
      '**En relation tog slut – inte din förmåga att älska:** Du kommer kunna älska igen.',
      '**Bättre förberedd:** Du vet nu mer om vad du vill och inte vill ha.',
      '**Red flags:** Du kommer känna igen varningssignaler tidigare.',
      '**Starkare gränser:** Du har lärt dig vad du accepterar och inte.',
      '**Ta det lugnt:** Det finns ingen brådska att hitta någon ny.',
      '**Njut av singellivet:** Det finns fördelar med att vara ensam – utforska dem.',
      '**När du är redo:** Du kommer märka när du är redo för kärlek igen.',
      '**Tro på det:** Många hittar djupare kärlek efter en separation.'
    ],
    questions: [
      'Vad har separationen lärt dig om kärlek?',
      'Vad vill du ha i en framtida relation?',
      'Vad vill du absolut inte ha?',
      'Hur vet du när du är redo igen?'
    ]
  }
];

export default function SeparationPage() {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  const [selectedTopic, setSelectedTopic] = useState(null);

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="separation-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/relations')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Separation</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12 text-center">
          <p className="text-muted-foreground">Uppgradera för att få tillgång till detta innehåll.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full mt-4">Uppgradera nu</Button>
        </main>
      </div>
    );
  }

  if (selectedTopic) {
    const topic = SEPARATION_CONTENT.find(t => t.id === selectedTopic);
    return (
      <div className="min-h-screen bg-background" data-testid="separation-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => setSelectedTopic(null)} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Separation</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
          <div className="bg-slate-100 rounded-2xl p-8">
            <topic.icon className="w-10 h-10 text-slate-600 mb-4" />
            <h2 className="font-heading text-2xl font-semibold mb-2">{topic.title}</h2>
            <p className="text-muted-foreground">{topic.description}</p>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-border/30 space-y-4">
            {topic.content.map((text, i) => (
              <p key={i} className="text-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
            ))}
          </div>

          <div className="bg-primary/5 rounded-2xl p-6 space-y-4">
            <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              Reflektionsfrågor
            </h3>
            {topic.questions.map((q, i) => (
              <div key={i} className="flex gap-3 items-start p-4 rounded-xl bg-white">
                <span className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary shrink-0">{i + 1}</span>
                <span>{q}</span>
              </div>
            ))}
          </div>

          

          <p className="text-xs text-muted-foreground text-center px-4">
            ⚕️ Vid djup depression eller självmordstankar, kontakta vården eller ring 112.
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="separation-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/category/relations')} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-xl font-light">Separation</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="text-center space-y-3">
          <HeartCrack className="w-12 h-12 mx-auto text-slate-500" />
          <h2 className="font-heading text-2xl font-semibold">Separation & skilsmässa</h2>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Hantera avslut på ett hälsosamt sätt och bygg upp dig själv igen.
          </p>
        </div>

        <div className="space-y-3">
          {SEPARATION_CONTENT.map((topic) => (
            <button
              key={topic.id}
              onClick={() => setSelectedTopic(topic.id)}
              className="w-full bg-white p-5 rounded-2xl border border-border/30 text-left hover:shadow-md transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center">
                  <topic.icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">{topic.title}</h3>
                  <p className="text-sm text-muted-foreground">{topic.description}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
              </div>
            </button>
          ))}
        </div>

        <div className="bg-slate-100 rounded-2xl p-4 border border-slate-200">
          <p className="text-slate-700 text-sm">
            💙 <strong>Kom ihåg:</strong> Att avsluta en relation som inte fungerar är inte att misslyckas – det är att välja dig själv.
          </p>
        </div>

        
      </main>
    </div>
  );
}
